import React from 'react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { FileText, AlertCircle, Printer, Edit } from 'lucide-react';
import axios from 'axios';

export const MedCardsViewModal = ({ editedMedCardContent, error, isEditingMedCards, loadDashboard, practice, setEditedMedCardContent, setIsEditingMedCards, setViewMedCardsModal, toast, viewMedCardsModal, viewingCards }) => (
  <>
    <Dialog open={viewMedCardsModal} onOpenChange={setViewMedCardsModal}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto print-content">
        <DialogHeader className="no-print">
          <DialogTitle className="flex items-center justify-between">
            <span>{viewingCards[0]?.name || 'Med Card'}</span>
            {viewingCards[0]?.isCustomized && (
              <Badge className="bg-blue-100 text-blue-800">Customized</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 print-content">
          {/* Practice Header - Always Visible */}
          <div className="bg-white p-6 mb-4 border-2 border-gray-200 rounded-lg print-header">
            <div className="text-center mb-4">
              {practice?.logoUrl && (
                <img src={practice.logoUrl} alt="Practice Logo" className="h-16 mx-auto mb-4" />
              )}
              <h1 className="text-xl font-bold text-gray-900">{practice?.name || 'Dental Practice'}</h1>
              {practice?.address && (
                <p className="text-sm text-gray-600">
                  {practice.address.street}, {practice.address.city}, {practice.address.state} {practice.address.zipCode}
                </p>
              )}
              {practice?.phone && (
                <p className="text-sm text-gray-700 font-medium mt-1">Phone: {practice.phone}</p>
              )}
            </div>
          </div>

          {isEditingMedCards ? (
            <div className="space-y-4 no-print">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Edit Medication Instructions
                </label>
                <p className="text-xs text-gray-600 mb-2">
                  Edit the medication instructions below. Your changes will be saved to your practice library.
                </p>
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-3">
                  <p className="text-xs text-yellow-800">
                    <strong>⚠️ Note:</strong> Any "Provider Notice" sections will be automatically removed from patient-facing cards.
                  </p>
                </div>
                <textarea
                  value={editedMedCardContent}
                  onChange={(e) => setEditedMedCardContent(e.target.value)}
                  className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm leading-relaxed"
                  placeholder="Edit medication instructions..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditingMedCards(false);
                    setEditedMedCardContent('');
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={async () => {
                  try {
                    const token = localStorage.getItem('dentalToken');
                    await axios.put(
                      `${process.env.REACT_APP_BACKEND_URL}/api/med-cards/${viewingCards[0].id}`,
                      { customContent: editedMedCardContent },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast({ title: "Saved", description: "Med Card updated" });
                    setViewMedCardsModal(false);
                    setIsEditingMedCards(false);
                    await loadDashboard();
                  } catch (error) {
                    toast({ title: "Error", description: "Failed to save", variant: "destructive" });
                  }
                }}>
                  Save to Library
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {viewingCards.map((selectedCard, cardIdx) => (
                <div key={cardIdx}>
                  {/* Base Medication Instructions - Professionally Formatted */}
                  {selectedCard?.baseContent && (
                    <div className="bg-white border-2 border-gray-300 rounded-xl shadow-sm mb-6">
                      {/* Header Section */}
                      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <h2 className="text-2xl font-bold mb-1">{selectedCard.name}</h2>
                            <p className="text-blue-100 text-sm">{selectedCard.category}</p>
                          </div>
                          <div className="bg-white/20 px-4 py-2 rounded-lg">
                            <FileText className="h-8 w-8" />
                          </div>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="p-8 space-y-6">
                        {(() => {
                          const content = selectedCard.baseContent;
                          const sections = [];

                          // Parse sections from content
                          const lines = content.split('\n');
                          let currentSection = { title: '', content: [] };

                          lines.forEach((line, index) => {
                            const trimmed = line.trim();

                            // Detect section headers
                            if (trimmed.match(/^(Why this medicine|How much to take|How to take it|Important reminders|Common side effects|Call your dentist|Provider notice)/i)) {
                              if (currentSection.title || currentSection.content.length > 0) {
                                sections.push({...currentSection});
                              }
                              currentSection = { title: trimmed, content: [] };
                            } else if (trimmed) {
                              currentSection.content.push(trimmed);
                            }
                          });

                          if (currentSection.title || currentSection.content.length > 0) {
                            sections.push(currentSection);
                          }

                          // Filter out "Provider Notice"
                          const patientSections = sections.filter(section => 
                            !section.title.toLowerCase().includes('provider notice')
                          );

                          return patientSections.map((section, idx) => {
                            const title = section.title.toLowerCase();
                            let bgColor = 'bg-gray-50';
                            let borderColor = 'border-gray-200';
                            let textColor = 'text-gray-900';
                            let iconColor = 'text-blue-600';

                            // Color code by section type
                            if (title.includes('important') || title.includes('do not')) {
                              bgColor = 'bg-yellow-50';
                              borderColor = 'border-yellow-300';
                              iconColor = 'text-yellow-600';
                            } else if (title.includes('call') || title.includes('seek help')) {
                              bgColor = 'bg-red-50';
                              borderColor = 'border-red-300';
                              iconColor = 'text-red-600';
                            } else if (title.includes('side effects')) {
                              bgColor = 'bg-orange-50';
                              borderColor = 'border-orange-200';
                              iconColor = 'text-orange-600';
                            }

                            return (
                              <div key={idx} className={`${bgColor} border-l-4 ${borderColor} rounded-r-lg p-5`}>
                                <div className="flex items-start space-x-3">
                                  <div className={`${iconColor} mt-1 flex-shrink-0`}>
                                    {title.includes('why') && <FileText className="h-5 w-5" />}
                                    {title.includes('how much') && <span className="text-lg font-bold">💊</span>}
                                    {title.includes('how to take') && <span className="text-lg">📋</span>}
                                    {title.includes('important') && <AlertCircle className="h-5 w-5" />}
                                    {title.includes('side effects') && <span className="text-lg">⚠️</span>}
                                    {title.includes('call') && <span className="text-lg">🚨</span>}
                                  </div>
                                  <div className="flex-1">
                                    {section.title && (
                                      <h3 className={`font-bold text-lg mb-2 ${textColor}`}>
                                        {section.title}
                                      </h3>
                                    )}
                                    <div className="space-y-2 text-gray-800 leading-relaxed">
                                      {section.content.map((line, lineIdx) => (
                                        <p key={lineIdx} className="text-sm">
                                          {line.startsWith('•') || line.startsWith('-') || line.startsWith('*') ? (
                                            <span className="flex items-start space-x-2">
                                              <span className="text-blue-600 font-bold mt-0.5">•</span>
                                              <span className="flex-1">{line.replace(/^[•*-]\s*/, '')}</span>
                                            </span>
                                          ) : (
                                            line
                                          )}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Custom Instructions (if any) */}
                  {selectedCard?.customContent && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 shadow-sm">
                      <div className="flex items-start space-x-3">
                        <div className="bg-blue-600 text-white p-2 rounded-lg">
                          <FileText className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-blue-900 text-lg mb-3">Practice-Specific Custom Instructions</h4>
                          <div className="text-sm text-blue-900 whitespace-pre-wrap leading-relaxed bg-white/50 p-4 rounded-lg">
                            {selectedCard.customContent}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="flex justify-end space-x-3 no-print">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditedMedCardContent(viewingCards[0]?.customContent || viewingCards[0]?.baseContent || '');
                    setIsEditingMedCards(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    // Use the print handler logic
                    const cardToPrint = viewingCards[0];
                    if (!cardToPrint) return;

                    // Import helper
                    import('../MedCardsPatientRecordsHelper').then(({ createPrintHTML }) => {
                      // Remove existing print area
                      const existingPrintArea = document.querySelector('.print-only');
                      if (existingPrintArea) {
                        existingPrintArea.remove();
                      }

                      // Create new print area
                      const printArea = document.createElement('div');
                      printArea.className = 'print-only';
                      printArea.innerHTML = createPrintHTML(cardToPrint, practice);
                      document.body.appendChild(printArea);

                      // Print
                      setTimeout(() => {
                        window.print();
                      }, 100);
                    });
                  }}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  </>
);
